import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { smsService } from '../../services/smsService';
import {
  SmsGlobalTemplate,
  SmsGlobalTemplateStep,
  SmsGlobalTemplateType,
} from '../../types/sms';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Modal from '../../components/UI/Modal';

type DelayUnit = 'hours' | 'days';

function toDisplayDelay(hours: number): { value: number; unit: DelayUnit } {
  if (hours >= 24 && hours % 24 === 0) {
    return { value: hours / 24, unit: 'days' };
  }
  return { value: hours, unit: 'hours' };
}

function fromDisplayDelay(value: number, unit: DelayUnit): number {
  const n = Math.max(0, Math.floor(value) || 0);
  return unit === 'days' ? n * 24 : n;
}

const emptyStep = (stepNumber: number): SmsGlobalTemplateStep => ({
  stepNumber,
  templateBody: '',
  delayHours: stepNumber === 1 ? 0 : 24,
});

const AdminSmsTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<SmsGlobalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SmsGlobalTemplate | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateType, setTemplateType] = useState<SmsGlobalTemplateType>('message');
  const [steps, setSteps] = useState<SmsGlobalTemplateStep[]>([emptyStep(1)]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await smsService.listAdminGlobalTemplates();
      setTemplates(data.templates || []);
    } catch {
      toast.error('Failed to load SMS templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setTemplateType('message');
    setSteps([emptyStep(1)]);
    setModalOpen(true);
  };

  const openEdit = (template: SmsGlobalTemplate) => {
    setEditing(template);
    setName(template.name);
    setDescription(template.description || '');
    setTemplateType(template.templateType);
    setSteps(
      template.steps.length > 0
        ? template.steps.map((s) => ({ ...s }))
        : [emptyStep(1)]
    );
    setModalOpen(true);
  };

  const handleTypeChange = (type: SmsGlobalTemplateType) => {
    setTemplateType(type);
    if (type === 'message') {
      setSteps([steps[0] || emptyStep(1)]);
    } else if (steps.length < 2) {
      setSteps([steps[0] || emptyStep(1), emptyStep(2)]);
    }
  };

  const updateStep = (stepNumber: number, patch: Partial<SmsGlobalTemplateStep>) => {
    setSteps((prev) =>
      prev.map((s) => (s.stepNumber === stepNumber ? { ...s, ...patch } : s))
    );
  };

  const updateStepDelay = (stepNumber: number, value: number, unit: DelayUnit) => {
    updateStep(stepNumber, { delayHours: fromDisplayDelay(value, unit) });
  };

  const addStep = () => {
    setSteps((prev) => [...prev, emptyStep(prev.length + 1)]);
  };

  const removeStep = (stepNumber: number) => {
    setSteps((prev) =>
      prev
        .filter((s) => s.stepNumber !== stepNumber)
        .map((s, i) => ({ ...s, stepNumber: i + 1 }))
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (steps.some((s) => !s.templateBody.trim())) {
      toast.error('All message steps need text');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        templateType,
        description: description.trim() || null,
        steps: steps.map((s) => ({
          stepNumber: s.stepNumber,
          templateBody: s.templateBody.trim(),
          delayHours: s.delayHours,
        })),
      };

      if (editing) {
        await smsService.updateGlobalTemplate(editing.id, payload);
        toast.success('Template updated');
      } else {
        await smsService.createGlobalTemplate(payload);
        toast.success('Template created');
      }
      setModalOpen(false);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save template';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (template: SmsGlobalTemplate) => {
    if (!window.confirm(`Deactivate "${template.name}"? It will no longer appear in campaign pickers.`)) {
      return;
    }
    try {
      await smsService.deactivateGlobalTemplate(template.id);
      toast.success('Template deactivated');
      await load();
    } catch {
      toast.error('Failed to deactivate template');
    }
  };

  const activeTemplates = templates.filter((t) => t.isActive);
  const inactiveTemplates = templates.filter((t) => !t.isActive);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">SMS Global Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create reusable SMS templates. Campaign editors can pick these when setting up message sequences.
          </p>
        </div>
        <Button onClick={openCreate}>
          <PlusIcon className="h-4 w-4 mr-1.5" />
          New template
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Active templates</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingSpinner />
          ) : activeTemplates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active templates yet.</p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-lg">
              {activeTemplates.map((t) => (
                <li key={t.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                  <div>
                    <div className="font-medium text-foreground">{t.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t.templateType === 'message' ? 'Single message' : `Sequence (${t.steps.length} steps)`}
                      {t.description ? ` · ${t.description}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outlined" size="sm" onClick={() => openEdit(t)}>
                      <PencilSquareIcon className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button variant="outlined" size="sm" onClick={() => handleDeactivate(t)}>
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Deactivate
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {!loading && inactiveTemplates.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-muted-foreground">Inactive templates</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border border border-border rounded-lg opacity-70">
              {inactiveTemplates.map((t) => (
                <li key={t.id} className="px-4 py-3 text-sm text-muted-foreground">
                  {t.name} ({t.templateType})
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit template' : 'New template'}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Name *</label>
            <input className="input w-full" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Description</label>
            <input
              className="input w-full"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional short label"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Type *</label>
            <select
              className="select w-full max-w-xs"
              value={templateType}
              onChange={(e) => handleTypeChange(e.target.value as SmsGlobalTemplateType)}
            >
              <option value="message">Single message</option>
              <option value="sequence">Full sequence</option>
            </select>
          </div>

          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Use {'{{firstName}}'}, {'{{lastName}}'}, {'{{companyName}}'} for personalization.
            </p>
            {steps.map((step) => {
              const isFirst = step.stepNumber === 1;
              const delayDisplay = toDisplayDelay(step.delayHours);
              return (
                <div key={step.stepNumber} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-foreground">
                      {isFirst ? 'Message 1' : `Follow-up ${step.stepNumber - 1}`}
                    </span>
                    {!isFirst && templateType === 'sequence' && (
                      <button
                        type="button"
                        className="text-xs text-destructive hover:underline"
                        onClick={() => removeStep(step.stepNumber)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <textarea
                    className="textarea min-h-[80px] w-full"
                    value={step.templateBody}
                    onChange={(e) => updateStep(step.stepNumber, { templateBody: e.target.value })}
                    placeholder="Hi {{firstName}}, ..."
                  />
                  {!isFirst && templateType === 'sequence' && (
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="w-24">
                        <label className="block text-xs text-muted-foreground mb-1">Wait</label>
                        <input
                          type="number"
                          className="input"
                          min={0}
                          value={delayDisplay.value}
                          onChange={(e) =>
                            updateStepDelay(
                              step.stepNumber,
                              Number(e.target.value) || 0,
                              delayDisplay.unit
                            )
                          }
                        />
                      </div>
                      <div className="w-28">
                        <label className="block text-xs text-muted-foreground mb-1">Unit</label>
                        <select
                          className="select"
                          value={delayDisplay.unit}
                          onChange={(e) =>
                            updateStepDelay(
                              step.stepNumber,
                              delayDisplay.value,
                              e.target.value as DelayUnit
                            )
                          }
                        >
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {templateType === 'sequence' && (
              <Button type="button" variant="secondary" size="sm" onClick={addStep}>
                <PlusIcon className="h-4 w-4 mr-1" />
                Add follow-up
              </Button>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outlined" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create template'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminSmsTemplatesPage;
